import { useMemo } from "react";
import {
  buildFormSchemaForCarriers,
  FieldType,
  PINNACLE_AVAILABLE_CARRIERS,
  isFieldVisible,
  DEPRECATED_FIELD_IDS,
} from "@aam/shared";
import { useFormStore } from "../store/formStore";
import { useAutofill } from "../context/AutofillContext";

const CARRIER_LABELS = new Map(PINNACLE_AVAILABLE_CARRIERS.map((c) => [c.value, c.label]));

function formatValue(fieldId: string, value: unknown, type: FieldType): string {
  if (value === undefined || value === null || value === "") return "—";
  if (fieldId === "selectedCarriers" && Array.isArray(value)) {
    return value.map((v) => CARRIER_LABELS.get(String(v)) ?? String(v)).join(", ") || "—";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ") || "—";
  if (typeof value === "object") {
    const file = value as { fileName?: string };
    if (file.fileName) return file.fileName;
    return JSON.stringify(value);
  }
  if (type === FieldType.SIGNATURE && String(value).startsWith("data:image/")) {
    return "Signature captured";
  }
  return String(value);
}

export function ReviewSummary() {
  const formData = useFormStore((s) => s.formData);
  const autofill = useAutofill();
  const carriers = (formData.selectedCarriers as string[]) ?? [];

  const rows = useMemo(() => {
    const schema = buildFormSchemaForCarriers(carriers);
    const items: Array<{ fieldId: string; label: string; value: string }> = [];

    for (const section of schema.sections) {
      if (section.id === "application_review") continue;

      for (const field of section.fields) {
        if (field.id === "reviewSummary") continue;
        if (DEPRECATED_FIELD_IDS.has(field.id)) continue;
        if (!isFieldVisible(field, formData)) continue;

        const raw = formData[field.id];
        if (raw === undefined || raw === null || raw === "") continue;

        items.push({
          fieldId: field.id,
          label: field.label,
          value: formatValue(field.id, raw, field.type),
        });
      }
    }

    return items;
  }, [carriers.join("|"), formData]);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border-default bg-surface-overlay p-4 text-sm text-content-muted">
        Complete the earlier steps to see your contract request summary here.
      </div>
    );
  }

  const needsConfirming = autofill?.reviewFieldIds ?? [];

  return (
    <div className="space-y-3">
      {needsConfirming.length > 0 && (
        <div
          className="rounded-xl border-2 border-status-pending-border bg-status-pending-bg p-4 text-sm text-status-pending-text"
          role="status"
        >
          <span className="font-semibold">
            {needsConfirming.length} carried-over{" "}
            {needsConfirming.length === 1 ? "answer" : "answers"} still to confirm.
          </span>{" "}
          They are tagged <span className="font-semibold">Confirm</span> below — open that step to
          check or change them before you submit.
        </div>
      )}
      <div className="rounded-xl border border-border-default bg-surface-overlay overflow-hidden">
        <dl className="divide-y divide-border-subtle">
          {rows.map((row) => {
            const carriedOver = autofill?.isAutofilled(row.fieldId) ?? false;
            const confirm = carriedOver && needsConfirming.includes(row.fieldId);
            return (
              <div key={row.fieldId} className="flex flex-col sm:flex-row sm:gap-4 px-4 py-3">
                <dt className="sm:w-44 shrink-0 text-xs font-medium uppercase tracking-wider text-content-muted">
                  {row.label}
                </dt>
                <dd className="flex flex-wrap items-center gap-2 text-sm text-content-primary break-words">
                  <span>{row.value}</span>
                  {carriedOver && (
                    <span
                      className={
                        confirm
                          ? "rounded border border-status-pending-border bg-status-pending-bg px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-status-pending-text"
                          : "rounded border border-border-default bg-surface-raised px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-content-muted"
                      }
                    >
                      {confirm ? "Confirm" : "Autofilled"}
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </div>
    </div>
  );
}
